import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Table, TableAvailability, TableWeekdayAvailability, CombinedTable, StrollerPolicy } from "@/types/table";
import { useAuth } from "@/contexts/AuthContext";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { combinedTablesQueryKey } from "@/hooks/queries/useCombinedTablesQuery";
import { useTableBookablePeriods } from "@/hooks/useTableBookablePeriods";
import { supabase } from "@/integrations/supabase/client";
import { formatCustomerName } from "@/lib/clientUtils";
import { TableCard } from "@/components/TableCard";
import { TableForm } from "@/components/TableForm";
import { TableListView } from "@/components/TableListView";
import { TableAvailabilityDialog } from "@/components/TableAvailabilityDialog";
import { DeleteTableDialog } from "@/components/DeleteTableDialog";
import { CombineTablesSheet } from "@/components/CombineTablesSheet";
import { CombinedTableCard } from "@/components/CombinedTableCard";
import { CombinedTableListView } from "@/components/CombinedTableListView";
import { CapacityImpactDialog } from "@/components/CapacityImpactDialog";
import { EditReservationDialog } from "@/components/EditReservationDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, UtensilsCrossed, ArrowLeft, Armchair, LayoutGrid, List, Wand2, HelpCircle, MoreVertical, ArrowUpDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PiktableLoader } from "@/components/PiktableLoader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AutoCombineTablesDialog } from "@/components/AutoCombineTablesDialog";
import { TableCombination } from "@/lib/tableCombinationUtils";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useNavigate } from "react-router-dom";
import { useFloorPlanZones } from "@/hooks/useFloorPlanZones";
import { useBookableZones } from "@/hooks/useBookableZones";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useFloorPlanPositions } from "@/hooks/useFloorPlanPositions";
import { FloorPlanSetupRequestDialog } from "@/components/FloorPlanSetupRequestDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TableManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { establishmentId, session } = useAuth();
  const { 
    tables, 
    combinedTables,
    reservations,
    timeSlots,
    availabilities, 
    weekdayAvailabilities, 
    dateOverrides,
    loading, 
    error,
    refetch,
    patchTable,
    patchCombinedTable
  } = useSupabaseData({ useAuthenticatedData: true, establishmentId: establishmentId || undefined, session });
  
  // Hook pour les périodes de non-réservabilité
  const { 
    bookablePeriods, 
    addBookablePeriod, 
    removeBookablePeriod 
  } = useTableBookablePeriods({ 
    establishmentId: establishmentId || undefined, 
    useAuthenticated: true 
  });
  
  const [hoveredCombinationId, setHoveredCombinationId] = useState<string | null>(null);
  
  // Floor plan zones (visual layout) — distinct from bookable zones
  const { zones: floorPlanZones } = useFloorPlanZones({ establishmentId: establishmentId || undefined });
  const { settings: reservationSettings, updateCustomAssignmentOrder } = useReservationSettings({ establishmentId: establishmentId || undefined });
  // Bookable zones (named reservable zones, e.g. Bistrot, Salon) — drives reservation filtering
  const { zones: bookableZones } = useBookableZones({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const { positions, assignToZone, savePositions } = useFloorPlanPositions(
    tables, 
    combinedTables, 
    { establishmentId: establishmentId || undefined }
  );

  // Handler for assigning a table to a FLOOR PLAN zone (visual layout)
  const handleAssignFloorPlanZone = useCallback(async (tableId: string, zoneId: string | null) => {
    assignToZone(tableId, zoneId);
    // Save the updated positions
    const updatedPositions = positions.map(p => 
      p.tableId === tableId ? { ...p, zoneId } : p
    );
    await savePositions(updatedPositions);
  }, [assignToZone, positions, savePositions]);

  // queryClient for fine-grained cache invalidations
  const queryClient = useQueryClient();

  // handleAssignBookableZone is defined after handleInlineUpdate below.

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tableManagement.viewMode');
      return (saved === 'list' || saved === 'grid') ? saved : 'grid';
    }
    return 'grid';
  });

  // Sorting state lifted from TableListView to persist across data refreshes
  type SortField = 'name' | 'capacity' | 'zone' | null;
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('tableManagement.viewMode', viewMode);
  }, [viewMode]);
  
  // Dialog states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [availabilityTable, setAvailabilityTable] = useState<Table | null>(null);
  const [deletingTable, setDeletingTable] = useState<Table | null>(null);
  const [combiningTable, setCombiningTable] = useState<Table | null>(null);
  const [isAutoCombineOpen, setIsAutoCombineOpen] = useState(false);
  const [deactivatingTable, setDeactivatingTable] = useState<Table | null>(null);
  const [editingReservationId, setEditingReservationId] = useState<string | null>(null);
  const [showFloorPlanRequest, setShowFloorPlanRequest] = useState(false);


  const filteredTables = tables;

  const stats = {
    total: tables.length,
    active: tables.filter((t) => t.isActive).length,
    inactive: tables.filter((t) => !t.isActive).length,
  };

  // Déterminer quelles tables sont concernées par la combinaison survolée
  const highlightedTableIds = hoveredCombinationId
    ? combinedTables.find((ct) => ct.id === hoveredCombinationId)?.tableIds || []
    : [];

  // Génère un nom unique pour une table dupliquée
  const generateDuplicateName = (originalName: string): string => {
    // Regex pour capturer le nom de base et un éventuel numéro à la fin
    const match = originalName.match(/^(.+?)(\s*\((\d+)\))?$/);
    const baseName = match ? match[1].trim() : originalName;
    
    // Trouver toutes les tables avec le même nom de base
    const existingNumbers: number[] = [];
    tables.forEach(t => {
      const tableMatch = t.name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s*\\((\\d+)\\))?$`));
      if (tableMatch) {
        const num = tableMatch[2] ? parseInt(tableMatch[2]) : 1;
        existingNumbers.push(num);
      }
    });
    
    // Trouver le prochain numéro disponible
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 2;
    return `${baseName} (${nextNumber})`;
  };

  const handleDuplicateTable = async (table: Table) => {
    try {
      const newName = generateDuplicateName(table.name);
      
      const { error } = await supabase
        .from('tables')
        .insert([{
          name: newName,
          capacity: table.capacity,
          notes: table.notes,
          is_active: table.isActive,
          establishment_id: establishmentId
        }]);

      if (error) throw error;

      await refetch();
      toast({
        title: "Table dupliquée",
        description: `${newName} a été créée à partir de ${table.name}.`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de dupliquer la table.",
        variant: "destructive",
      });
    }
  };

  const handleAddTable = async (data: Omit<Table, "id" | "createdAt">) => {
    try {
      const { error } = await supabase
        .from('tables')
        .insert([{
          name: data.name,
          capacity: data.capacity,
          min_capacity: data.minCapacity ?? 1,
          notes: data.notes,
          is_active: data.isActive,
          is_bookable: data.isBookable,
          allows_children: data.allowsChildren ?? true,
          allows_stroller: data.allowsStroller ?? true,
          is_terrace: data.isTerrace ?? false,
          zone_id: data.zoneId ?? null,
          establishment_id: establishmentId
        }]);

      if (error) throw error;

      await refetch();
      toast({
        title: "Table ajoutée",
        description: `${data.name} a été ajoutée avec succès.`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la table.",
        variant: "destructive",
      });
    }
  };

  // Map common Supabase / Postgres errors to manager-friendly messages.
  const describeSupabaseError = (err: any): string => {
    const code = err?.code || err?.status;
    const raw = err?.message || "";
    if (code === "42501" || raw.toLowerCase().includes("permission denied") || raw.toLowerCase().includes("row-level security")) {
      return "Accès refusé. Reconnecte-toi puis réessaie.";
    }
    if (code === "PGRST301" || raw.toLowerCase().includes("jwt") || raw.toLowerCase().includes("expired")) {
      return "Session expirée. Reconnecte-toi pour continuer.";
    }
    if (code === "23503") {
      return "Référence introuvable (la zone a peut-être été supprimée). Recharge la page.";
    }
    if (code === "23505") {
      return "Conflit de données (valeur déjà utilisée).";
    }
    return raw ? `Impossible de modifier la table : ${raw}` : "Impossible de modifier la table.";
  };

  // Inline update for list view (used by zone select, switches, capacity, etc.)
  const handleInlineUpdate = useCallback(async (tableId: string, updates: Partial<Table>) => {
    // Snapshot previous values for targeted rollback
    const previous = tables.find((t) => t.id === tableId);
    const previousSnapshot: Partial<Table> = previous ? Object.keys(updates).reduce((acc, key) => {
      (acc as any)[key] = (previous as any)[key];
      return acc;
    }, {} as Partial<Table>) : {};

    try {
      // Optimistic update — patch local cache immediately
      patchTable(tableId, updates);

      const updatePayload: Record<string, any> = {};
      if (updates.name !== undefined) updatePayload.name = updates.name;
      if (updates.capacity !== undefined) updatePayload.capacity = updates.capacity;
      if (updates.minCapacity !== undefined) updatePayload.min_capacity = updates.minCapacity;
      if (updates.isBookable !== undefined) updatePayload.is_bookable = updates.isBookable;
      if (updates.isActive !== undefined) updatePayload.is_active = updates.isActive;
      if (updates.allowsChildren !== undefined) updatePayload.allows_children = updates.allowsChildren;
      if (updates.allowsStroller !== undefined) updatePayload.allows_stroller = updates.allowsStroller;
      if (updates.isTerrace !== undefined) updatePayload.is_terrace = updates.isTerrace;
      if (updates.assignmentPriority !== undefined) updatePayload.assignment_priority = updates.assignmentPriority;
      const zoneIdChanged = (updates as any).zoneId !== undefined;
      if (zoneIdChanged) updatePayload.zone_id = (updates as any).zoneId;

      const { error } = await supabase
        .from('tables')
        .update(updatePayload)
        .eq('id', tableId);

      if (error) throw error;

      // If zone changed, the DB trigger may have recomputed combined_tables.zone_id.
      // Invalidate the combined-tables cache so the UI reflects the new heritage.
      if (zoneIdChanged && establishmentId) {
        queryClient.invalidateQueries({ queryKey: combinedTablesQueryKey(establishmentId) });
      }
    } catch (error: any) {
      // Targeted rollback — revert only the fields we tried to change
      if (previous) {
        patchTable(tableId, previousSnapshot);
      }
      // Surface the real cause in the console for debugging
      console.error('[TableManagement] handleInlineUpdate failed', {
        tableId,
        updates,
        code: error?.code,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        status: error?.status,
      });
      toast({
        title: "Erreur",
        description: describeSupabaseError(error),
        variant: "destructive",
      });
      throw error;
    }
  }, [tables, patchTable, establishmentId, queryClient, toast]);

  // Handler for assigning a table to a BOOKABLE zone (reservation filter)
  const handleAssignBookableZone = useCallback(async (tableId: string, zoneId: string | null) => {
    await handleInlineUpdate(tableId, { zoneId } as Partial<Table>);
  }, [handleInlineUpdate]);


  const handleUpdateTable = async (data: Omit<Table, "id" | "createdAt">) => {
    if (!editingTable) return;
    
    try {
      const { error } = await supabase
        .from('tables')
        .update({
          name: data.name,
          capacity: data.capacity,
          min_capacity: data.minCapacity ?? 1,
          notes: data.notes,
          is_active: data.isActive,
          is_bookable: data.isBookable,
          allows_children: data.allowsChildren ?? true,
          allows_stroller: data.allowsStroller ?? true,
          is_terrace: data.isTerrace ?? false,
          zone_id: (data as any).zoneId ?? null
        })
        .eq('id', editingTable.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Table modifiée",
        description: `${data.name} a été modifiée avec succès.`,
      });
      setEditingTable(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de modifier la table.",
        variant: "destructive",
      });
    }
  };

  const [deactivatingFutureReservations, setDeactivatingFutureReservations] = useState<any[]>([]);
  const [deactivatingFutureCount, setDeactivatingFutureCount] = useState(0);

  const handleToggleActive = async (id: string) => {
    const table = tables.find((t) => t.id === id);
    if (!table) return;

    // Si la table est active et on veut la désactiver, demander confirmation
    if (table.isActive) {
      // Vérifier les réservations futures avant de désactiver
      try {
        const { checkTableReservationImpact } = await import("@/lib/reservationProtectionUtils");
        const impact = await checkTableReservationImpact(id);
        setDeactivatingFutureReservations(impact.futureReservations);
        setDeactivatingFutureCount(impact.futureCount);
      } catch (error) {
        console.error('Erreur lors de la vérification des réservations:', error);
        setDeactivatingFutureReservations([]);
        setDeactivatingFutureCount(0);
      }
      setDeactivatingTable(table);
      return;
    }

    // Sinon, activer directement
    patchTable(id, { isActive: true });
    try {
      const { error } = await supabase
        .from('tables')
        .update({ is_active: true })
        .eq('id', id);

      if (error) {
        await refetch();
        throw error;
      }
      toast({
        title: "Table activée",
        description: `${table.name} est maintenant activée.`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'activer la table.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivatingTable) return;

    patchTable(deactivatingTable.id, { isActive: false });
    try {
      const { error } = await supabase
        .from('tables')
        .update({ is_active: false })
        .eq('id', deactivatingTable.id);

      if (error) {
        await refetch();
        throw error;
      }
      toast({
        title: "Table désactivée",
        description: `${deactivatingTable.name} est maintenant désactivée.`,
      });
      setDeactivatingTable(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de désactiver la table.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTable = async () => {
    if (!deletingTable) return;

    try {
      const { error } = await supabase
        .from('tables')
        .delete()
        .eq('id', deletingTable.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Table supprimée",
        description: `${deletingTable.name} a été supprimée définitivement.`,
        variant: "destructive",
      });
      setDeletingTable(null);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error?.message || "Impossible de supprimer la table.",
        variant: "destructive",
      });
    }
  };

  const handleAddBlocked = async (tableId: string, date: Date, reason?: string) => {
    try {
      const { error } = await supabase
        .from('table_availability')
        .insert([{
          table_id: tableId,
          date: format(date, 'yyyy-MM-dd'),
          is_available: false,
          reason,
          establishment_id: establishmentId
        }]);

      if (error) throw error;

      await refetch();
      toast({
        title: "Date bloquée",
        description: "La date a été bloquée avec succès.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de bloquer la date.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveBlocked = async (id: string) => {
    try {
      const { error } = await supabase
        .from('table_availability')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Date débloquée",
        description: "La date est maintenant disponible.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de débloquer la date.",
        variant: "destructive",
      });
    }
  };

  const handleSetWeekdayAvailability = async (
    tableId: string,
    availableDays: number[],
    reason?: string
  ) => {
    try {
      const existing = weekdayAvailabilities.find((w) => w.tableId === tableId);

      if (existing) {
        const { error } = await supabase
          .from('table_weekday_availability')
          .update({
            available_days: availableDays,
            reason
          })
          .eq('table_id', tableId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('table_weekday_availability')
          .insert([{
            table_id: tableId,
            available_days: availableDays,
            reason,
            establishment_id: establishmentId
          }]);

        if (error) throw error;
      }

      await refetch();
      toast({
        title: "Disponibilité mise à jour",
        description: "Les jours d'ouverture de la table ont été modifiés.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la disponibilité.",
        variant: "destructive",
      });
    }
  };

  const handleCreateCombination = async (tableIds: string[]) => {
    try {
      const selectedTables = tables.filter((t) => tableIds.includes(t.id));
      const totalCapacity = selectedTables.reduce((sum, t) => sum + t.capacity, 0);
      const name = selectedTables.map((t) => t.name).join(" + ");

      // Appeler la fonction PostgreSQL qui gère toute la transaction atomiquement
      const { data: combinedTableId, error } = await supabase.rpc('create_combined_table', {
        p_name: name,
        p_total_capacity: totalCapacity,
        p_table_ids: tableIds,
        p_establishment_id: establishmentId
      });

      if (error) throw error;

      await refetch();
      toast({
        title: "Combinaison créée",
        description: `${name} a été créée avec succès (${totalCapacity} places).`,
      });
    } catch (error) {
      console.error('Error creating combination:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la combinaison.",
        variant: "destructive",
      });
    }
  };

  // Handler for batch creating multiple combinations (auto-generate)
  const handleCreateMultipleCombinations = async (combinations: TableCombination[]) => {
    let successCount = 0;
    let errorCount = 0;

    for (const combo of combinations) {
      try {
        // Use effectiveCapacity if set, otherwise use totalCapacity
        const capacityToUse = combo.effectiveCapacity ?? combo.totalCapacity;
        
        const { error } = await supabase.rpc('create_combined_table', {
          p_name: combo.name,
          p_total_capacity: capacityToUse,
          p_table_ids: combo.tableIds,
          p_establishment_id: establishmentId
        });

        if (error) throw error;
        successCount++;
      } catch (error) {
        console.error('Error creating combination:', combo.name, error);
        errorCount++;
      }
    }

    await refetch();

    if (errorCount === 0) {
      toast({
        title: "Combinaisons créées",
        description: `${successCount} combinaison${successCount > 1 ? "s" : ""} créée${successCount > 1 ? "s" : ""} avec succès.`,
      });
    } else {
      toast({
        title: "Création partielle",
        description: `${successCount} créée(s), ${errorCount} erreur(s).`,
        variant: "destructive",
      });
    }
  };

  // Handler for updating combined table name
  const handleUpdateCombinedName = async (id: string, newName: string) => {
    try {
      patchCombinedTable(id, { name: newName });
      const { error } = await supabase
        .from('combined_tables')
        .update({ name: newName })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Nom modifié",
        description: `Table combinée renommée : "${newName}".`,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier le nom.",
        variant: "destructive",
      });
    }
  };

  // Handler for updating combined table capacity
  const handleUpdateCombinedCapacity = async (id: string, newCapacity: number) => {
    const combination = combinedTables.find((ct) => ct.id === id);
    if (!combination) return;

    // Check for reservations that would exceed the new capacity
    if (newCapacity < combination.totalCapacity) {
      try {
        const { checkCapacityExceeded } = await import("@/lib/reservationProtectionUtils");
        const result = await checkCapacityExceeded(id, newCapacity, true);
        
        if (result.hasExceeded) {
          toast({
            title: "Modification impossible",
            description: `${result.exceededReservations.length} réservation(s) dépassent cette capacité. Réassignez-les d'abord.`,
            variant: "destructive",
          });
          return;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de capacité:', error);
      }
    }

    try {
      patchCombinedTable(id, { totalCapacity: newCapacity });
      const { error } = await supabase
        .from('combined_tables')
        .update({ total_capacity: newCapacity })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Capacité modifiée",
        description: `Capacité mise à jour : ${newCapacity} places.`,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier la capacité.",
        variant: "destructive",
      });
    }
  };

  const handleToggleCombinedActive = async (id: string) => {
    const combination = combinedTables.find((ct) => ct.id === id);
    if (!combination) return;

    try {
      patchCombinedTable(id, { isActive: !combination.isActive });
      const { error } = await supabase
        .from('combined_tables')
        .update({ is_active: !combination.isActive })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: combination.isActive ? "Combinaison désactivée" : "Combinaison activée",
        description: `${combination.name} est maintenant ${combination.isActive ? "désactivée" : "activée"}.`,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier la combinaison.",
        variant: "destructive",
      });
    }
  };

  const handleToggleCombinedBookable = async (id: string) => {
    const combination = combinedTables.find((ct) => ct.id === id);
    if (!combination) return;

    try {
      patchCombinedTable(id, { isBookable: !combination.isBookable });
      const { error } = await supabase
        .from('combined_tables')
        .update({ is_bookable: !combination.isBookable })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: combination.isBookable ? "Hors réservation activé" : "Réservation activée",
        description: `${combination.name} est maintenant ${combination.isBookable ? "hors réservation" : "réservable"}.`,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut de réservation.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCombinedAllowsChildren = async (id: string, value: boolean) => {
    try {
      patchCombinedTable(id, { allowsChildren: value });
      const { error } = await supabase
        .from('combined_tables')
        .update({ allows_children: value })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier le paramètre enfants.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCombinedAllowsStroller = async (id: string, policy: StrollerPolicy) => {
    try {
      patchCombinedTable(id, { strollerPolicy: policy, allowsStroller: policy !== 'deny' });
      const { error } = await supabase
        .from('combined_tables')
        .update({ stroller_policy: policy, allows_stroller: policy !== 'deny' } as any)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier le paramètre poussettes.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCombinedIsTerrace = async (id: string, value: boolean) => {
    try {
      patchCombinedTable(id, { isTerrace: value });
      const { error } = await supabase
        .from('combined_tables')
        .update({ is_terrace: value })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier le paramètre terrasse.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCombinedMinCapacity = async (id: string, newMinCapacity: number) => {
    const combination = combinedTables.find((ct) => ct.id === id);
    if (!combination) return;

    if (newMinCapacity > combination.totalCapacity) {
      toast({
        title: "Erreur",
        description: "La capacité minimum ne peut pas dépasser la capacité maximale.",
        variant: "destructive",
      });
      return;
    }

    try {
      patchCombinedTable(id, { minCapacity: newMinCapacity });
      const { error } = await supabase
        .from('combined_tables')
        .update({ min_capacity: newMinCapacity })
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Capacité minimum modifiée",
        description: `Capacité minimum mise à jour : ${newMinCapacity} personne(s).`,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier la capacité minimum.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCombinedAssignmentPriority = async (id: string, newPriority: number) => {
    try {
      patchCombinedTable(id, { assignmentPriority: newPriority });
      const { error } = await supabase
        .from('combined_tables')
        .update({ assignment_priority: newPriority })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de modifier la priorité.",
        variant: "destructive",
      });
    }
  };

  // Drag-and-drop reorder for table priorities
  const handleReorderTablePriorities = useCallback(async (orderedIds: string[]) => {
    // Optimistic update
    orderedIds.forEach((id, index) => {
      patchTable(id, { assignmentPriority: index });
    });

    // Batch update in DB
    try {
      const updates = orderedIds.map((id, index) =>
        supabase
          .from('tables')
          .update({ assignment_priority: index })
          .eq('id', id)
      );
      const results = await Promise.all(updates);
      const failed = results.find(r => r.error);
      if (failed?.error) throw failed.error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de réorganiser les priorités.",
        variant: "destructive",
      });
    }
  }, [patchTable, refetch, toast]);

  // Drag-and-drop reorder for combined table priorities
  const handleReorderCombinedPriorities = useCallback(async (orderedIds: string[]) => {
    // Optimistic update
    orderedIds.forEach((id, index) => {
      patchCombinedTable(id, { assignmentPriority: index });
    });

    // Batch update in DB
    try {
      const updates = orderedIds.map((id, index) =>
        supabase
          .from('combined_tables')
          .update({ assignment_priority: index })
          .eq('id', id)
      );
      const results = await Promise.all(updates);
      const failed = results.find(r => r.error);
      if (failed?.error) throw failed.error;
    } catch (error) {
      await refetch();
      toast({
        title: "Erreur",
        description: "Impossible de réorganiser les priorités.",
        variant: "destructive",
      });
    }
  }, [patchCombinedTable, refetch, toast]);

  const handleDeleteCombination = async (id: string) => {
    const combination = combinedTables.find((ct) => ct.id === id);
    if (!combination) return;

    // Vérifier les réservations futures avant de supprimer
    try {
      const { checkCombinedTableReservationImpact } = await import("@/lib/reservationProtectionUtils");
      const impact = await checkCombinedTableReservationImpact(id);
      
      if (impact.hasFutureReservations) {
        toast({
          title: "Suppression impossible",
          description: `Cette combinaison a ${impact.futureCount} réservation(s) future(s). Désactivez-la d'abord ou annulez les réservations.`,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des réservations:', error);
    }

    try {
      const { error } = await supabase
        .from('combined_tables')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Combinaison supprimée",
        description: `${combination.name} a été supprimée.`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la combinaison.",
        variant: "destructive",
      });
    }
  };

  const [showRecalculateWarning, setShowRecalculateWarning] = useState(false);
  const [recalculateExceeded, setRecalculateExceeded] = useState<any[]>([]);
  const [recalculatingCombination, setRecalculatingCombination] = useState<{
    id: string;
    name: string;
    oldCapacity: number;
    newCapacity: number;
  } | null>(null);

  const handleRecalculateCombinedCapacity = async (combinedTableId: string) => {
    try {
      const combination = combinedTables.find(ct => ct.id === combinedTableId);
      if (!combination) return;

      // Recalculer la capacité totale
      const componentTables = tables.filter(t => combination.tableIds.includes(t.id));
      const correctCapacity = componentTables.reduce((sum, t) => sum + t.capacity, 0);

      // Si la capacité diminue, vérifier les réservations
      if (correctCapacity < combination.totalCapacity) {
        try {
          const { checkCapacityExceeded } = await import("@/lib/reservationProtectionUtils");
          const result = await checkCapacityExceeded(combinedTableId, correctCapacity, true);
          
          if (result.hasExceeded) {
            setRecalculatingCombination({
              id: combinedTableId,
              name: combination.name,
              oldCapacity: combination.totalCapacity,
              newCapacity: correctCapacity
            });
            setRecalculateExceeded(result.exceededReservations);
            setShowRecalculateWarning(true);
            return;
          }
        } catch (error) {
          console.error('Erreur lors de la vérification de capacité:', error);
        }
      }

      // Mettre à jour dans la base de données
      const { error } = await supabase
        .from('combined_tables')
        .update({ total_capacity: correctCapacity })
        .eq('id', combinedTableId);

      if (error) throw error;

      await refetch();
      toast({
        title: "Capacité recalculée",
        description: `Capacité mise à jour : ${correctCapacity} places.`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de recalculer la capacité.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmRecalculate = async () => {
    if (!recalculatingCombination) return;

    try {
      const { error } = await supabase
        .from('combined_tables')
        .update({ total_capacity: recalculatingCombination.newCapacity })
        .eq('id', recalculatingCombination.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Capacité recalculée",
        description: `Capacité mise à jour : ${recalculatingCombination.newCapacity} places.`,
      });
      setShowRecalculateWarning(false);
      setRecalculatingCombination(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de recalculer la capacité.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <PiktableLoader message="Chargement des tables..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-6 max-w-md">
          <div className="text-center space-y-4">
            <p className="text-destructive">Erreur de chargement</p>
            <Button onClick={() => refetch()}>Réessayer</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <UtensilsCrossed className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                <span className="truncate">Gestion des tables</span>
              </h1>
              <p className="text-muted-foreground text-sm hidden sm:block">
                Gérez les tables physiques du café et créez des combinaisons
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
              className="bg-muted rounded-md p-1"
            >
              <ToggleGroupItem value="grid" aria-label="Vue grille" size="sm">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="Vue liste" size="sm">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
            
            {/* Desktop buttons */}
            <Button variant="outline" onClick={() => setShowFloorPlanRequest(true)} className="hidden sm:flex">
              <HelpCircle className="w-4 h-4 mr-2" />
              Assistance plan de salle
            </Button>
            <Button variant="outline" onClick={() => setIsAutoCombineOpen(true)} className="hidden sm:flex">
              <Wand2 className="w-4 h-4 mr-2" />
              Générer combinaisons
            </Button>
            
            {/* Mobile dropdown for secondary actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="sm:hidden">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowFloorPlanRequest(true)}>
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Assistance plan de salle
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsAutoCombineOpen(true)}>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Générer combinaisons
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button onClick={() => {
              setEditingTable(null);
              setIsFormOpen(true);
            }} className="flex-1 sm:flex-none">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Ajouter une table</span>
              <span className="sm:hidden">Ajouter</span>
            </Button>
          </div>
        </div>

        {/* Priority toggle - visible in list mode only */}
        {viewMode === 'list' && filteredTables.length > 0 && (
          <div className="flex items-center gap-3 px-1">
            <div className="flex items-center gap-2">
              <Switch
                id="custom-assignment-order"
                checked={reservationSettings?.customAssignmentOrderEnabled ?? false}
                onCheckedChange={(checked) => updateCustomAssignmentOrder(checked)}
              />
              <Label htmlFor="custom-assignment-order" className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                Ordre de priorité personnalisé
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>Quand activé, les tables avec une priorité plus basse (1, 2, 3…) seront assignées en premier lors des réservations automatiques. Les tables sans priorité (0) sont assignées en dernier.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}

        {/* Tables View */}
        {filteredTables.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <UtensilsCrossed className="w-12 h-12 mx-auto text-muted-foreground" />
              <div>
                <p className="text-lg font-medium mb-2">Aucune table</p>
                <p className="text-sm text-muted-foreground">
                  Commencez par ajouter votre première table.
                </p>
              </div>
              <Button onClick={() => setIsFormOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une table
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'list' ? (
          <TableListView
            tables={filteredTables}
            weekdayAvailabilities={weekdayAvailabilities}
            onUpdate={handleInlineUpdate}
            onToggleActive={handleToggleActive}
            onManageAvailability={(t) => setAvailabilityTable(t)}
            onDelete={(t) => setDeletingTable(t)}
            onDuplicate={handleDuplicateTable}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={(field, direction) => {
              setSortField(field);
              setSortDirection(direction);
            }}
            zones={bookableZones}
            onAssignZone={handleAssignBookableZone}
            showChildrenColumn={reservationSettings?.childrenEnabled}
            showStrollerColumn={reservationSettings?.strollerEnabled}
            showTerraceColumn={reservationSettings?.terraceEnabled}
            showPriorityColumn={reservationSettings?.customAssignmentOrderEnabled}
            onReorderPriorities={handleReorderTablePriorities}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTables.map((table) => (
              <TableCard
                key={table.id}
                table={table}
                weekdayAvailability={weekdayAvailabilities.find((w) => w.tableId === table.id)}
                zone={table.zoneId ? (bookableZones.find(z => z.id === table.zoneId) ?? null) : null}
                onToggleActive={handleToggleActive}
                onEdit={(t) => {
                  setEditingTable(t);
                  setIsFormOpen(true);
                }}
                onManageAvailability={(t) => setAvailabilityTable(t)}
                onDelete={(t) => setDeletingTable(t)}
                onCombine={(t) => setCombiningTable(t)}
                onDuplicate={handleDuplicateTable}
                onCapacityChange={(t, delta) => handleInlineUpdate(t.id, { capacity: Math.max(1, t.capacity + delta) })}
                isHighlighted={highlightedTableIds.includes(table.id)}
              />
            ))}
          </div>
        )}

        {/* Section Tables Combinées */}
        {combinedTables.length > 0 && (
          <div className="mt-12 pt-8 border-t">
            <div className="mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
                <Armchair className="w-6 h-6" />
                Tables combinées
              </h2>
              <p className="text-muted-foreground text-sm">
                Configurations créées en combinant plusieurs tables physiques
              </p>
            </div>
            {viewMode === 'list' ? (
              <CombinedTableListView
                combinedTables={combinedTables}
                allTables={tables}
                onToggleActive={handleToggleCombinedActive}
                onToggleBookable={handleToggleCombinedBookable}
                onDelete={handleDeleteCombination}
                onRecalculate={handleRecalculateCombinedCapacity}
                onUpdateCapacity={handleUpdateCombinedCapacity}
                onUpdateName={handleUpdateCombinedName}
                onUpdateAllowsChildren={handleUpdateCombinedAllowsChildren}
                onUpdateAllowsStroller={handleUpdateCombinedAllowsStroller}
                onUpdateIsTerrace={handleUpdateCombinedIsTerrace}
                onUpdateMinCapacity={handleUpdateCombinedMinCapacity}
                onUpdateAssignmentPriority={handleUpdateCombinedAssignmentPriority}
                showChildrenColumn={reservationSettings?.childrenEnabled ?? false}
                showStrollerColumn={reservationSettings?.strollerEnabled ?? false}
                showTerraceColumn={reservationSettings?.terraceEnabled ?? false}
                showPriorityColumn={reservationSettings?.customAssignmentOrderEnabled ?? false}
                onHover={setHoveredCombinationId}
                onReorderPriorities={handleReorderCombinedPriorities}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {combinedTables.map((combinedTable) => (
                  <div
                    key={combinedTable.id}
                    onMouseEnter={() => setHoveredCombinationId(combinedTable.id)}
                    onMouseLeave={() => setHoveredCombinationId(null)}
                  >
                    <CombinedTableCard
                      combinedTable={combinedTable}
                      allTables={tables}
                      onToggleActive={handleToggleCombinedActive}
                      onToggleBookable={handleToggleCombinedBookable}
                      onDelete={handleDeleteCombination}
                      onRecalculate={handleRecalculateCombinedCapacity}
                      onUpdateCapacity={handleUpdateCombinedCapacity}
                      onUpdateName={handleUpdateCombinedName}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <TableForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        table={editingTable}
        establishmentId={establishmentId}
        onSave={editingTable ? handleUpdateTable : handleAddTable}
      />

      <TableAvailabilityDialog
        open={!!availabilityTable}
        onOpenChange={(open) => !open && setAvailabilityTable(null)}
        table={availabilityTable}
        availabilities={availabilities}
        weekdayAvailabilities={weekdayAvailabilities}
        bookablePeriods={bookablePeriods}
        onAddBlocked={handleAddBlocked}
        onRemoveBlocked={handleRemoveBlocked}
        onSetWeekdayAvailability={handleSetWeekdayAvailability}
        onAddBookablePeriod={addBookablePeriod}
        onRemoveBookablePeriod={removeBookablePeriod}
        onEditReservation={(reservationId) => {
          setEditingReservationId(reservationId);
          setAvailabilityTable(null);
        }}
        establishmentId={establishmentId || undefined}
      />

      <DeleteTableDialog
        open={!!deletingTable}
        onOpenChange={(open) => !open && setDeletingTable(null)}
        table={deletingTable}
        combinedTables={combinedTables}
        onConfirm={handleDeleteTable}
        onDeactivateInstead={handleToggleActive}
      />

      <CombineTablesSheet
        open={!!combiningTable}
        onOpenChange={(open) => !open && setCombiningTable(null)}
        currentTable={combiningTable}
        allTables={tables}
        onCreateCombination={handleCreateCombination}
      />

      <AutoCombineTablesDialog
        open={isAutoCombineOpen}
        onOpenChange={setIsAutoCombineOpen}
        tables={tables}
        combinedTables={combinedTables}
        onCreateCombinations={handleCreateMultipleCombinations}
      />

      <AlertDialog open={!!deactivatingTable} onOpenChange={(open) => !open && setDeactivatingTable(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Désactiver cette table ?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Vous êtes sur le point de désactiver <strong>{deactivatingTable?.name}</strong>.
              </p>
              
              {deactivatingFutureCount > 0 ? (
                <div className="bg-yellow-500/10 p-3 rounded-md border border-yellow-500/20">
                  <p className="font-medium text-yellow-700 dark:text-yellow-400 mb-2">
                    ⚠️ {deactivatingFutureCount} réservation(s) future(s) sont actuellement assignées à cette table.
                  </p>
                  <ul className="space-y-1 text-sm">
                    {deactivatingFutureReservations.slice(0, 3).map(res => {
                      const resDate = new Date(res.date);
                      return (
                        <li key={res.id}>
                          • {resDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à{" "}
                          {res.startTime.slice(0, 5)} - {res.customerName}
                        </li>
                      );
                    })}
                    {deactivatingFutureCount > 3 && (
                      <li className="text-muted-foreground italic">
                        ... et {deactivatingFutureCount - 3} autre(s)
                      </li>
                    )}
                  </ul>
                  <p className="text-sm mt-2">
                    Les réservations resteront valides, mais vous ne pourrez pas gérer cette table pour de nouvelles réservations.
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  Cette table ne sera plus disponible pour les nouvelles réservations. Les réservations existantes ne seront pas affectées.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeactivate}>
              Confirmer la désactivation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CapacityImpactDialog
        open={showRecalculateWarning}
        onOpenChange={setShowRecalculateWarning}
        tableName={recalculatingCombination?.name || ""}
        oldCapacity={recalculatingCombination?.oldCapacity || 0}
        newCapacity={recalculatingCombination?.newCapacity || 0}
        exceededReservations={recalculateExceeded}
        onConfirm={handleConfirmRecalculate}
      />

      {editingReservationId && (() => {
        const reservation = reservations.find(r => r.id === editingReservationId);
        if (!reservation) return null;
        
        return (
          <EditReservationDialog
            open={!!editingReservationId}
            onOpenChange={(open) => {
              if (!open) {
                setEditingReservationId(null);
              }
            }}
            reservation={{
              id: parseInt(reservation.id),
              name: formatCustomerName(reservation.customerFirstName, reservation.customerLastName),
              email: reservation.customerEmail,
              phone: reservation.customerPhone,
              persons: reservation.numberOfPeople,
              tableId: reservation.tableId,
              combinedTableId: reservation.combinedTableId,
              time: reservation.startTime,
              reservationDate: reservation.date,
              notes: reservation.notes,
              internalNotes: reservation.internalNotes,
              status: reservation.status,
            }}
            timeSlots={timeSlots.map(ts => ({ start: ts.start, end: ts.end }))}
            dateOverrides={dateOverrides}
            allTables={tables}
            allReservations={reservations.map(r => ({
              id: parseInt(r.id),
              tableId: r.tableId,
              combinedTableId: r.combinedTableId,
              time: r.startTime,
              reservationDate: r.date,
            }))}
            availabilities={availabilities}
            weekdayAvailabilities={weekdayAvailabilities}
            combinedTables={combinedTables}
            existingClients={[]}
            onReservationUpdate={async (reservationId, updates) => {
              try {
                const response = await fetch(
                  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-reservation/${reservationId}`,
                  {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                    },
                    body: JSON.stringify(updates),
                  }
                );

                if (!response.ok) throw new Error('Failed to update reservation');

                await refetch();
                setEditingReservationId(null);
                toast({
                  title: "Réservation modifiée",
                  description: "La réservation a été mise à jour avec succès.",
                });
              } catch (error) {
                toast({
                  title: "Erreur",
                  description: "Impossible de modifier la réservation.",
                  variant: "destructive",
                });
              }
            }}
            onClientUpdate={() => {}}
            childrenEnabled={reservationSettings?.childrenEnabled}
          />
        );
      })()}

      {/* Floor Plan Setup Request Dialog */}
      <FloorPlanSetupRequestDialog
        open={showFloorPlanRequest}
        onOpenChange={setShowFloorPlanRequest}
        tables={tables}
        onRefetchTables={refetch}
      />
    </div>
  );
}
